import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-modal";
import moment from "moment";
import {
  X,
  SquarePen,
  Copy,
  Mail,
  Eye,
  Zap,
  FileText,
  User,
  Package,
  Calendar,
} from "lucide-react";

moment.locale("es");

const money = (n) => {
  const val = Number(n || 0);
  return val.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const safeText = (v, fallback = "---") =>
  v === undefined || v === null || `${v}`.trim() === "" ? fallback : `${v}`;

const parseDate = (d) => {
  if (!d) return null;
  if (d?.seconds) return new Date(d.seconds * 1000);
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};

const formatDate = (d) => {
  const dt = parseDate(d);
  if (!dt) return "---";
  return moment(dt).format("D MMMM [de] YYYY").replace(".", "");
};

const STATUS_BADGE_CLASS = {
  Pendiente: "badge-status status-pendiente",
  Aprobado: "badge-status status-aprobado",
  Rechazado: "badge-status status-rechazado",
  Vencido: "badge-status status-vencido",
};

const modalStyle = {
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    zIndex: 1300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
  },
  content: {
    position: "relative",
    inset: "auto",
    width: "min(1120px, 96vw)",
    height: "min(92vh, 860px)",
    border: "none",
    borderRadius: "16px",
    padding: 0,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 25px 60px rgba(2, 6, 23, 0.25)",
    fontFamily: "Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif",
  },
};

function IconBtn({ tooltip, className = "", onClick, children }) {
  return (
    <button
      data-tooltip-down={tooltip}
      className={`mpc-icon-btn ${className}`}
      type="button"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export default function ModalPreviaCotizacion({
  isOpen,
  onClose,
  cotizacion,
  onEdit,
  onCopy,
  onSendEmail,
  onView,
  onQuickAction,
}) {
  const c = useMemo(() => cotizacion ?? {}, [cotizacion]);
  const [convertOpen, setConvertOpen] = useState(false);
  const convertBtnRef = useRef(null);
  const convertMenuRef = useRef(null);

  // Cierra el menú al hacer click fuera
  useEffect(() => {
    if (!convertOpen) return;

    const onDown = (e) => {
      const btn = convertBtnRef.current;
      const menu = convertMenuRef.current;

      if (btn && btn.contains(e.target)) return;
      if (menu && menu.contains(e.target)) return;

      setConvertOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [convertOpen]);

  // Si cambias de cotización o cierras modal, cierra dropdown
  useEffect(() => {
    if (!isOpen) setConvertOpen(false);
  }, [isOpen, cotizacion]);

  const items = useMemo(() => {
    // 1) Fuente real (la que guardas)
    const source =
      c.productosSeleccionados ||
      c.partidas ||
      c.conceptos ||
      c.items ||
      c.productos ||
      c.detalleProductos ||
      [];

    const arr = Array.isArray(source) ? source : [];

    // 2) Normaliza para que la tabla SIEMPRE tenga las mismas llaves
    const normalized = arr.map((p, idx) => {
      const cantidad = Number(p?.cantidad ?? p?.qty ?? 0);

      // En tu form guardas "tarifa" y "importe"
      const precioUnitario = Number(
        p?.precioUnitario ?? p?.precio ?? p?.tarifa ?? 0,
      );

      // Si viene "importe" úsalo; si no, calcula
      const subtotal = Number.isFinite(Number(p?.subtotal))
        ? Number(p.subtotal)
        : Number.isFinite(Number(p?.importe))
          ? Number(p.importe)
          : cantidad * precioUnitario;

      const nombre = p?.nombre || p?.producto || p?.concepto || "";
      const descripcion = p?.descripcion || "";

      return {
        id: p?.id || `${idx}`,
        sku: p?.sku || p?.SKU || p?.codigo || p?.clave || "---",
        cantidad,
        unidad: p?.unidad || p?.um || p?.medida || "Pieza",
        nombre: nombre || (descripcion ? descripcion : "---"),
        descripcion: descripcion || (nombre ? nombre : "---"),
        precioUnitario,
        subtotal,
      };
    });

    // 3) Si no hay nada real, aquí ya NO mostramos demo (para que veas el "No hay partidas")
    return normalized;
  }, [c]);

  const computed = useMemo(() => {
    const subtotalCalc = items.reduce((acc, it) => {
      const sub =
        it?.subtotal ??
        Number(it?.cantidad || 0) *
          Number(it?.precioUnitario || it?.precio || 0);
      return acc + Number(sub || 0);
    }, 0);

    const subtotal = Number(
      c.subtotalBase ?? c.subtotal ?? c.subtotalCalc ?? subtotalCalc ?? 0,
    );

    const baseIVA = Number(
      c.baseIVA ??
        c.subtotalConDesc ?? // ✅ este es el bueno en tu data actual
        Number(c.total ?? 0) - Number(c.iva ?? 0) ??
        Math.max(0, subtotal),
    );

    let descuentoTotal = Number(c.descuentoTotal ?? c.descuentoMonto ?? 0);

    if (!descuentoTotal && subtotal > 0) {
      descuentoTotal = Math.max(0, subtotal - baseIVA);
    }

    if (!descuentoTotal && Number(c.descuento || 0) > 0) {
      const tipo = c.tipoDescuentoGlobal ?? "%";
      const descVal = Number(c.descuento || 0);

      const calc =
        tipo === "%" ? subtotal * (descVal / 100) : Math.min(descVal, subtotal);

      descuentoTotal = Math.max(0, calc);
    }

    const ivaPct = Number(c.ivaPct ?? c.ivaPorcentaje ?? c.ivaPct ?? 16);
    const iva = Number(c.iva ?? (baseIVA * ivaPct) / 100);
    const total = Number(c.total ?? baseIVA + iva);

    return { subtotal, descuentoTotal, baseIVA, ivaPct, iva, total };
  }, [c, items]);

  const folio =
    c.numeroCotizacionFormatted || c.numeroCotizacion || c.folio || "—";

  const estado = safeText(c.estado, "Pendiente");
  const divisa = safeText(c.divisa, "MXN");
  const creadoPorDisplay = safeText(
    c.creadoPorNombre ||
      c.creadoPor ||
      c.createdByName ||
      c.createdBy?.name ||
      c.creadoPorEmail ||
      c.createdBy?.email,
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyle}
      ariaHideApp={false}
    >
      <div className="mpc-root">
        <div className="mpc-header">
          <div className="mpc-header-left">
            <h2 className="mpc-title">Detalles de Cotización</h2>

            <div className="mpc-badges">
              <span className="mpc-chip">
                <FileText size={14} />
                <span>{folio}</span>
              </span>

              <span
                className={
                  STATUS_BADGE_CLASS[estado] || "badge-status status-pendiente"
                }
              >
                {estado}
              </span>
            </div>
          </div>

          <div className="mpc-header-right">
            <IconBtn tooltip="Editar" onClick={() => onEdit?.(c)}>
              <SquarePen size={18} />
            </IconBtn>

            <IconBtn tooltip="Duplicar" onClick={() => onCopy?.(c)}>
              <Copy size={18} />
            </IconBtn>

            <IconBtn
              tooltip="Enviar por Correo"
              onClick={() => onSendEmail?.(c)}
            >
              <Mail size={18} />
            </IconBtn>

            <IconBtn tooltip="Ver como PDF" onClick={() => onView?.(c)}>
              <Eye size={18} />
            </IconBtn>

            <div className="mpc-convert-wrap">
              <button
                ref={convertBtnRef}
                data-tooltip-down="Convertir a"
                className="mpc-icon-btn mpc-icon-primary"
                type="button"
                onClick={() => setConvertOpen((v) => !v)}
              >
                <Zap size={18} />
              </button>

              {convertOpen ? (
                <div
                  ref={convertMenuRef}
                  className="mpc-convert-menu"
                  role="menu"
                >
                  <button
                    type="button"
                    className="mpc-convert-item"
                    onClick={() => {
                      setConvertOpen(false);
                      onQuickAction?.(c, "pedido");
                    }}
                  >
                    Pedido
                  </button>

                  <button
                    type="button"
                    className="mpc-convert-item"
                    onClick={() => {
                      setConvertOpen(false);
                      onQuickAction?.(c, "factura");
                    }}
                  >
                    Factura
                  </button>

                  <button
                    type="button"
                    className="mpc-convert-item"
                    onClick={() => {
                      setConvertOpen(false);
                      onQuickAction?.(c, "remision");
                    }}
                  >
                    Remisión
                  </button>
                </div>
              ) : null}
            </div>

            <span className="mpc-header-divider" aria-hidden="true" />

            <button
              data-tooltip-down="Cerrar"
              className="mpc-close"
              type="button"
              onClick={onClose}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="mpc-body">
          <Section title="Datos Generales" icon={<FileText size={16} />}>
            <div className="mpc-grid-3">
              <Field label="Folio" value={safeText(folio)} />
              <Field
                label="Fecha de Emisión"
                value={formatDate(c.fechaCotizacion || c.fechaEmision)}
                icon="calendar"
              />
              <Field
                label="Válida Hasta"
                value={formatDate(c.validaHasta || c.fechaVencimiento)}
                icon="calendar"
              />
            </div>

            <div className="mpc-grid-2">
              <Field
                label="Asunto"
                value={safeText(c.referencia || c.asunto)}
              />
              <Field label="Moneda" value={divisa} />
            </div>
          </Section>

          <Section
            title="Datos del Cliente"
            icon={<User size={16} />}
            className="mpc-section-client"
          >
            <div className="mpc-card">
              <div className="mpc-card-grid-2">
                <div className="mpc-card-item">
                  <div className="mpc-card-label">Cliente</div>
                  <div className="mpc-card-value">
                    {safeText(c.nombreCliente || c.cliente)}
                  </div>
                </div>

                <div className="mpc-card-item">
                  <div className="mpc-card-label">RFC</div>
                  <div className="mpc-card-value mpc-mono">
                    {safeText(c.clienteRfc || c.rfc)}
                  </div>
                </div>
              </div>

              <div className="mpc-card-item">
                <div className="mpc-card-label">Dirección</div>
                <div className="mpc-card-value">
                  {safeText(
                    c.direccionCliente ||
                      c.direccion ||
                      c.direccionFiscal ||
                      [
                        c.domicilio || c.calle || "",
                        c.numeroExt || c.noExt || c.numeroExterior
                          ? `#${c.numeroExt || c.noExt || c.numeroExterior}`
                          : "",
                        c.numeroInt || c.noInt || c.numeroInterior
                          ? `Int ${c.numeroInt || c.noInt || c.numeroInterior}`
                          : "",
                        c.colonia || c.col || "",
                        c.ciudad || c.municipio || "",
                        c.codigoPostal || c.cp
                          ? `CP ${c.codigoPostal || c.cp}`
                          : "",
                      ]
                        .filter(Boolean)
                        .join(", ") ||
                      [c.clienteLinea1, c.clienteLinea2]
                        .filter(Boolean)
                        .join(" "),
                  )}
                </div>
              </div>

              <div className="mpc-card-grid-3">
                <div className="mpc-card-item">
                  <div className="mpc-card-label">Contacto</div>
                  <div className="mpc-card-value">
                    {safeText(
                      c.clienteContacto ||
                        c.atencion ||
                        c.contactoPersonal ||
                        c.contacto ||
                        c.nombreContacto,
                    )}
                  </div>
                </div>

                <div className="mpc-card-item">
                  <div className="mpc-card-label">Teléfono</div>
                  <div className="mpc-card-value">
                    {safeText(c.clienteTelefono || c.telefono)}
                  </div>
                </div>

                <div className="mpc-card-item">
                  <div className="mpc-card-label">Correo Electrónico</div>
                  <div className="mpc-card-value">
                    {safeText(c.clienteEmail || c.correo || c.email)}
                  </div>
                </div>
              </div>
            </div>
          </Section>

          <Section title="Conceptos / Partidas" icon={<Package size={16} />}>
            <div className="mpc-table-wrap">
              <table className="mpc-table">
                <thead>
                  <tr>
                    <th className="mpc-th-sku">SKU</th>
                    <th className="t-center">Cantidad</th>
                    <th className="mpc-th-unit">Unidad</th>
                    <th className="mpc-th-desc">Descripción</th>
                    <th className="t-right">Precio Unitario</th>
                    <th className="t-right">Subtotal</th>
                  </tr>
                </thead>

                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="mpc-empty">
                        No hay partidas registradas.
                      </td>
                    </tr>
                  ) : (
                    items.map((it, idx) => {
                      const sku = safeText(
                        it.sku || it.SKU || it.codigo || it.clave,
                      );
                      const cantidad = Number(it.cantidad ?? it.qty ?? 0);
                      const unidad = safeText(it.unidad || it.um || it.medida);
                      const pu = Number(it.precioUnitario ?? it.precio ?? 0);
                      const sub = Number(it.subtotal) || Number(cantidad * pu);

                      return (
                        <tr key={it.id || `${sku}-${idx}`}>
                          <td className="mpc-td-sku">{sku}</td>
                          <td className="t-center">{cantidad || 0}</td>
                          <td className="mpc-td-unit mpc-td-unit-left">
                            {unidad}
                          </td>
                          <td className="mpc-td-desc">
                            <div className="mpc-desc-stack">
                              <div className="mpc-desc-title">
                                {safeText(
                                  it.nombre || it.producto || it.descripcion,
                                )}
                              </div>

                              {it.descripcion ? (
                                <div className="mpc-desc-text">
                                  {safeText(it.descripcion)}
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td className="t-right mpc-td-amt">${money(pu)}</td>
                          <td className="t-right mpc-td-amt">${money(sub)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="mpc-divider" aria-hidden="true" />

            <div className="mpc-totals">
              <div className="mpc-total-row">
                <span>Subtotal</span>
                <span>${money(computed.subtotal)}</span>
              </div>

              <div className="mpc-total-row">
                <span>Descuento Total</span>
                <span
                  className={
                    Math.abs(computed.descuentoTotal) > 0
                      ? "mpc-total-discount"
                      : "mpc-total-value"
                  }
                >
                  {Math.abs(computed.descuentoTotal) > 0 ? "- " : ""}$
                  {money(Math.abs(computed.descuentoTotal))}
                </span>
              </div>

              <div className="mpc-total-divider-soft" aria-hidden="true" />

              <div className="mpc-total-row">
                <span>Base IVA</span>
                <span>${money(computed.baseIVA)}</span>
              </div>

              <div className="mpc-total-row">
                <span>IVA ({computed.ivaPct}%)</span>
                <span>+ ${money(computed.iva)}</span>
              </div>

              <div className="mpc-total-divider-strong">
                <div className="mpc-total-big">
                  <span>Total</span>
                  <div className="mpc-total-amount">
                    <span className="mpc-total-currency">{divisa}</span>
                    <span className="mpc-total-number">
                      ${money(computed.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          <Section title="Observaciones" icon={<FileText size={16} />}>
            <div className="mpc-grid-1">
              <Field
                label="Tiempo de Entrega"
                value={safeText(c.tiempoEntrega)}
              />
            </div>

            <div className="mpc-grid-1">
              <Field
                label="Condiciones de Pago"
                value={safeText(c.condicionesPago)}
              />
            </div>

            <div className="mpc-grid-1">
              <Field label="Notas" value={safeText(c.notas)} />
            </div>

            <div className="mpc-divider" aria-hidden="true" />

            <div className="mpc-footer-cards">
              <GrayCard label="Vendedor" value={safeText(c.vendedor)} />
              <GrayCard label="Creado Por" value={creadoPorDisplay} />
              <GrayCard label="Versión" value={safeText(c.version, "v1")} />
            </div>
          </Section>
        </div>
      </div>
    </Modal>
  );
}

function Section({ title, icon, children, className }) {
  return (
    <div className={`mpc-section ${className || ""}`}>
      <div className="mpc-section-title">
        {icon}
        <span>{title}</span>
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, icon }) {
  return (
    <div className="mpc-field">
      <div className="mpc-label">{label}</div>

      <div className={icon ? "mpc-value-row" : undefined}>
        {icon === "calendar" ? (
          <Calendar size={14} className="mpc-date-ico" />
        ) : null}
        <div className="mpc-value">{safeText(value)}</div>
      </div>
    </div>
  );
}

function GrayCard({ label, value }) {
  return (
    <div className="mpc-gray">
      <label className="mpc-gray-label">{safeText(label)}</label>
      <p className="mpc-gray-value">{safeText(value)}</p>
    </div>
  );
}
